<template>
  <b-modal
    id="modal-import-pasien"
    title="Import Data Pasien"
    size="lg"
    centered
    no-close-on-backdrop
    @hidden="reset"
  >
    <!-- Step 1: Download Template & Upload -->
    <div v-if="step === 'idle'">
      <b-alert variant="info" show class="mb-75">
        <div class="alert-body">
          <feather-icon icon="InfoIcon" class="mr-50" />
          <span>
            Unduh template Excel, isi data pasien, lalu unggah kembali file yang sudah diisi.
            Kolom bertanda <strong>(*)</strong> wajib diisi.
          </span>
        </div>
      </b-alert>

      <div class="d-flex align-items-center mb-1">
        <b-button
          variant="outline-primary"
          size="sm"
          :disabled="downloading"
          @click="downloadTemplate"
        >
          <b-spinner v-if="downloading" small class="mr-50" />
          <feather-icon v-else icon="DownloadIcon" class="mr-50" />
          <span>Unduh Template Excel</span>
        </b-button>
      </div>

      <hr class="my-75">

      <div class="mb-50">
        <label class="font-weight-bold mb-50 d-block">Unggah File Excel</label>
        <div
          class="upload-zone d-flex flex-column align-items-center justify-content-center p-1"
          :class="{ 'drag-over': isDragOver }"
          @dragover.prevent="isDragOver = true"
          @dragleave.prevent="isDragOver = false"
          @drop.prevent="handleDrop"
          @click="$refs.fileInput.click()"
        >
          <feather-icon icon="UploadCloudIcon" size="32" class="text-primary mb-50" />
          <p class="mb-25 text-center">
            <span class="text-primary font-weight-bold">Klik untuk pilih file</span>
            atau drag &amp; drop di sini
          </p>
          <small class="text-muted">Format: .xlsx, .xls (maks. 10MB)</small>
        </div>
        <input
          ref="fileInput"
          type="file"
          accept=".xlsx,.xls"
          style="display: none"
          @change="handleFileSelect"
        >
      </div>

      <div v-if="selectedFile" class="d-flex align-items-center mt-50">
        <feather-icon icon="FileTextIcon" class="mr-50 text-success" />
        <span class="mr-auto">{{ selectedFile.name }}</span>
        <b-button variant="flat-danger" size="sm" @click="selectedFile = null">
          <feather-icon icon="XIcon" size="14" />
        </b-button>
      </div>
    </div>

    <!-- Step 2: Uploading -->
    <div v-else-if="step === 'uploading'" class="text-center py-1">
      <b-spinner variant="primary" class="mb-75" style="width: 2.5rem; height: 2.5rem" />
      <p class="mb-50">Mengimpor data pasien...</p>
      <b-progress
        :value="uploadProgress"
        :max="100"
        variant="primary"
        animated
        class="mb-50"
        style="height: 8px"
      />
      <small class="text-muted">{{ uploadProgress < 100 ? uploadProgress + '%' : 'Memproses...' }}</small>
    </div>

    <!-- Step 3: Results -->
    <div v-else-if="step === 'result'">
      <b-alert
        :variant="result && result.created_count > 0 ? 'success' : 'warning'"
        show
        class="mb-75"
      >
        <div class="alert-body">
          <feather-icon
            :icon="result && result.created_count > 0 ? 'CheckCircleIcon' : 'AlertTriangleIcon'"
            class="mr-50"
          />
          <span>{{ resultMessage }}</span>
        </div>
      </b-alert>

      <!-- Stats Cards -->
      <b-row class="mb-75">
        <b-col cols="4">
          <b-card class="text-center mb-0 border-success" no-body>
            <b-card-body class="py-75 px-50">
              <h3 class="mb-0 text-success">{{ result && result.created_count || 0 }}</h3>
              <small>Berhasil</small>
            </b-card-body>
          </b-card>
        </b-col>
        <b-col cols="4">
          <b-card class="text-center mb-0 border-warning" no-body>
            <b-card-body class="py-75 px-50">
              <h3 class="mb-0 text-warning">{{ result && result.skipped_count || 0 }}</h3>
              <small>Dilewati</small>
            </b-card-body>
          </b-card>
        </b-col>
        <b-col cols="4">
          <b-card class="text-center mb-0 border-danger" no-body>
            <b-card-body class="py-75 px-50">
              <h3 class="mb-0 text-danger">{{ totalErrors }}</h3>
              <small>Gagal</small>
            </b-card-body>
          </b-card>
        </b-col>
      </b-row>

      <!-- Skipped NIKs -->
      <div v-if="result && result.skipped_niks && result.skipped_niks.length > 0" class="mb-75">
        <b-button
          v-b-toggle.import-pasien-skipped
          variant="flat-warning"
          size="sm"
          block
          class="text-left"
        >
          <feather-icon icon="ChevronDownIcon" class="mr-25" />
          NIK yang dilewati (sudah terdaftar)
        </b-button>
        <b-collapse id="import-pasien-skipped">
          <b-list-group class="mt-25" flush>
            <b-list-group-item
              v-for="(nik, idx) in result.skipped_niks"
              :key="idx"
              class="py-25 px-50"
            >
              <small>{{ nik }}</small>
            </b-list-group-item>
          </b-list-group>
        </b-collapse>
      </div>

      <!-- Validation Failures -->
      <div v-if="result && result.failures && result.failures.length > 0" class="mb-75">
        <b-button
          v-b-toggle.import-pasien-failures
          variant="flat-danger"
          size="sm"
          block
          class="text-left"
        >
          <feather-icon icon="ChevronDownIcon" class="mr-25" />
          Detail baris gagal validasi
        </b-button>
        <b-collapse id="import-pasien-failures">
          <b-list-group class="mt-25" flush>
            <b-list-group-item
              v-for="(f, idx) in result.failures"
              :key="idx"
              class="py-25 px-50"
            >
              <small>
                <strong>Baris {{ f.row }}:</strong>
                {{ f.errors.join(', ') }}
              </small>
            </b-list-group-item>
          </b-list-group>
        </b-collapse>
      </div>

      <!-- Processing Errors -->
      <div v-if="result && result.errors && result.errors.length > 0" class="mb-75">
        <b-button
          v-b-toggle.import-pasien-errors
          variant="flat-danger"
          size="sm"
          block
          class="text-left"
        >
          <feather-icon icon="ChevronDownIcon" class="mr-25" />
          Detail error
        </b-button>
        <b-collapse id="import-pasien-errors">
          <b-list-group class="mt-25" flush>
            <b-list-group-item
              v-for="(err, idx) in result.errors"
              :key="idx"
              class="py-25 px-50"
            >
              <small>{{ err }}</small>
            </b-list-group-item>
          </b-list-group>
        </b-collapse>
      </div>
    </div>

    <!-- Footer -->
    <template #modal-footer="{ cancel }">
      <template v-if="step === 'idle'">
        <b-button variant="flat-secondary" size="sm" @click="cancel">
          Batal
        </b-button>
        <b-button
          variant="primary"
          size="sm"
          :disabled="!selectedFile"
          @click="uploadFile"
        >
          <feather-icon icon="UploadIcon" class="mr-50" />
          Import
        </b-button>
      </template>

      <template v-else-if="step === 'uploading'">
        <b-button variant="flat-secondary" size="sm" disabled>
          Mengimpor...
        </b-button>
      </template>

      <template v-else-if="step === 'result'">
        <b-button variant="flat-secondary" size="sm" @click="reset">
          Import Lagi
        </b-button>
        <b-button variant="primary" size="sm" @click="close">
          Selesai
        </b-button>
      </template>
    </template>
  </b-modal>
</template>

<script>
import { defineComponent, ref, computed } from '@vue/composition-api'
import {
  BModal,
  BButton,
  BAlert,
  BSpinner,
  BProgress,
  BRow,
  BCol,
  BCard,
  BCardBody,
  BCollapse,
  BListGroup,
  BListGroupItem,
  VBToggle,
} from 'bootstrap-vue'
import axios from '@axios'
import { useImportPasien } from '@/composables/useImportPasien'

export default defineComponent({
  name: 'ModalImportPasien',
  components: {
    BModal,
    BButton,
    BAlert,
    BSpinner,
    BProgress,
    BRow,
    BCol,
    BCard,
    BCardBody,
    BCollapse,
    BListGroup,
    BListGroupItem,
  },
  directives: {
    'b-toggle': VBToggle,
  },
  setup(props, { root }) {
    const { downloading, downloadTemplate } = useImportPasien(root)

    const step = ref('idle') // idle | uploading | result
    const selectedFile = ref(null)
    const uploadProgress = ref(0)
    const result = ref(null)
    const resultMessage = ref('')
    const isDragOver = ref(false)

    const totalErrors = computed(() => {
      if (!result.value) return 0
      const failureCount = result.value.failures ? result.value.failures.length : 0
      const errorCount = result.value.errors ? result.value.errors.length : 0
      return failureCount + errorCount
    })

    const handleFileSelect = (event) => {
      const file = event.target.files[0]
      if (file) {
        validateAndSetFile(file)
      }
      // Reset input so same file can be re-selected
      event.target.value = ''
    }

    const handleDrop = (event) => {
      isDragOver.value = false
      const file = event.dataTransfer.files[0]
      if (file) {
        validateAndSetFile(file)
      }
    }

    const validateAndSetFile = (file) => {
      const validTypes = [
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        'application/vnd.ms-excel',
      ]
      const validExts = ['.xlsx', '.xls']
      const ext = file.name.toLowerCase().slice(file.name.lastIndexOf('.'))

      if (!validTypes.includes(file.type) && !validExts.includes(ext)) {
        root.$dekatoast.error('Format file tidak valid. Gunakan .xlsx atau .xls')
        return
      }
      if (file.size > 10 * 1024 * 1024) {
        root.$dekatoast.error('Ukuran file maksimal 10MB')
        return
      }
      selectedFile.value = file
    }

    const uploadFile = async () => {
      if (!selectedFile.value) return

      step.value = 'uploading'
      uploadProgress.value = 0

      const formData = new FormData()
      formData.append('file', selectedFile.value)

      try {
        const response = await axios.post('/pasien/import', formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
          onUploadProgress: (progressEvent) => {
            if (progressEvent.total) {
              // Cap at 90% to leave visual room for server-side processing
              uploadProgress.value = Math.min(
                Math.round((progressEvent.loaded * 100) / progressEvent.total),
                90
              )
            }
          },
        })

        uploadProgress.value = 100
        result.value = response.data.data
        resultMessage.value = response.data.message
        step.value = 'result'

        // Refresh patient table if any patients were created
        if (result.value && result.value.created_count > 0) {
          root.$store.dispatch('pasien/getAllRows')
        }
      } catch (error) {
        step.value = 'idle'
        uploadProgress.value = 0
        root.$dekatoast.error(error)
      }
    }

    const reset = () => {
      step.value = 'idle'
      selectedFile.value = null
      uploadProgress.value = 0
      result.value = null
      resultMessage.value = ''
      isDragOver.value = false
    }

    const close = () => {
      root.$bvModal.hide('modal-import-pasien')
    }

    return {
      step,
      selectedFile,
      downloading,
      uploadProgress,
      result,
      resultMessage,
      isDragOver,
      totalErrors,
      downloadTemplate,
      handleFileSelect,
      handleDrop,
      uploadFile,
      reset,
      close,
    }
  },
})
</script>

<style lang="scss" scoped>
.upload-zone {
  border: 2px dashed #d8d6de;
  border-radius: 0.428rem;
  cursor: pointer;
  transition: all 0.2s ease;
  min-height: 120px;

  &:hover,
  &.drag-over {
    border-color: #7367f0;
    background-color: rgba(115, 103, 240, 0.04);
  }
}
</style>
